<template>
  <q-page padding class="column">
    <div class="col full-width">
      <q-card>
        <q-form @submit.prevent="update">

          <q-card-section>
            <div class="text-h6">{{ taskType?.name || 'Тип задачи' }}</div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-input dense v-model.trim="updateTaskType.name" label="Имя" :rules="[requiredRule]"/>
            <q-input dense v-model.trim="updateTaskType.description" type="textarea" label="Описание"/>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-checkbox v-model="enableGenerationStrategy" label="Периодическая"/>
          </q-card-section>

          <q-card-section class="q-pt-none" v-if="enableGenerationStrategy">
            <q-select v-model="generationStrategy.repeatUnit" :options="daysOptions"
                      label="Время" emit-value behavior="menu"
                      map-options/>
            <q-input v-model.trim="generationStrategy.repeatDelay" label="Значение" :rules="[requiredRule]"/>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-checkbox v-model="enableWaitingStrategyStrategy" label="Ожидание"/>
          </q-card-section>

          <q-card-section class="q-pt-none" v-if="enableWaitingStrategyStrategy">
            <q-select v-model="waitingStrategy.waitUnit" :options="waitOptions"
                      label="Время" emit-value behavior="menu"
                      map-options/>
            <q-input v-model.trim="waitingStrategy.waitDelay" label="Значение" :rules="[requiredRule]"/>
          </q-card-section>

          <q-card-actions align="right" class="text-primary">
            <q-btn no-caps flat label="Обновить" type="submit"/>
          </q-card-actions>
        </q-form>
        <q-form @submit.prevent="updateProperties">
          <q-card-section>
            <div class="text-h6">Дополнительные поля</div>
          </q-card-section>

          <q-card-section>
            <q-btn @click="addProperty" label="Добавить" color="primary"/>
          </q-card-section>
          <q-card-section v-for="property in additionalProperties" class="q-gutter-md">
            <q-input dense v-model.trim="property.name" label="Имя" :rules="[requiredRule]"/>
            <q-input dense v-model.trim="property.description" type="textarea" label="Описание"/>
            <q-select v-model="property.type" :options="propertyOptions"
                      label="Тип" emit-value behavior="menu"
                      map-options/>
            <q-btn @click="removeProperty(property)" label="Удалить" color="negative"/>
          </q-card-section>

          <q-card-actions align="right" class="text-primary">
            <q-btn no-caps flat label="Обновить" type="submit"/>
          </q-card-actions>
        </q-form>
      </q-card>
    </div>

  </q-page>
</template>

<script setup lang="ts">
import {useQuasar} from "quasar";
import {useRoute, useRouter} from "vue-router";
import {computed, reactive, watch, ref} from "vue";
import {requiredRule, propertyCodeRule} from "../util/rules";
import {daysOptions, waitOptions} from "../util/days";
import {propertyOptions} from "../util/property";
import {useTaskType, useUpdateTaskType, useUpdateTaskTypeProperties} from "../composables/task";
import {UpdateTaskType, GenerationStrategy, WaitingStrategy, TaskPropertyDescription} from "../models/task";
import cyrillicToTranslit from "cyrillic-to-translit-js";

const $q = useQuasar()
const route = useRoute()
const router = useRouter()

const projectId = computed(() => Number(route.params.projectId))
const taskTypeId = computed(() => Number(route.params.taskTypeId))

const {data: taskType, execute: refresh} = useTaskType(projectId, taskTypeId)

const updateTaskType: UpdateTaskType = reactive({
  name: "",
  description: "",
  waitingStrategy: null,
  generationStrategy: null
})
const additionalProperties = ref<TaskPropertyDescription[]>([])

const {execute: executeUpdate} = useUpdateTaskType(projectId, taskTypeId, updateTaskType)
const {execute: executeUpdateProperties} = useUpdateTaskTypeProperties(projectId, taskTypeId, additionalProperties)


const enableGenerationStrategy = ref(false)
const generationStrategy = reactive<GenerationStrategy>({
  repeatDelay: 1,
  repeatUnit: "DAYS"
})

const enableWaitingStrategyStrategy = ref(false)
const waitingStrategy = reactive<WaitingStrategy>({
  waitDelay: 1,
  waitUnit: "DAYS"
})

watch(taskType, (value) => {
  if (value) {
    updateTaskType.name = value.name
    updateTaskType.description = value.description || ""

    if (value.generationStrategy) {
      enableGenerationStrategy.value = true
      generationStrategy.repeatDelay = value.generationStrategy.repeatDelay
      generationStrategy.repeatUnit = value.generationStrategy.repeatUnit
    } else {
      enableGenerationStrategy.value = false
    }

    if (value.waitingStrategy) {
      enableWaitingStrategyStrategy.value = true
      waitingStrategy.waitDelay = value.waitingStrategy.waitDelay
      waitingStrategy.waitUnit = value.waitingStrategy.waitUnit
    } else {
      enableWaitingStrategyStrategy.value = false
    }

    additionalProperties.value = value.propertyDescription
  }
})


async function update() {
  if (enableWaitingStrategyStrategy.value) {
    updateTaskType.waitingStrategy = waitingStrategy
  } else {
    updateTaskType.waitingStrategy = null
  }
  if (enableGenerationStrategy.value) {
    updateTaskType.generationStrategy = generationStrategy
  } else {
    updateTaskType.generationStrategy = null
  }

  await executeUpdate(true)
  await refresh()
  $q.dialog({
    title: "Обновление",
    message: "Поля были обновлены"
  })
}


async function updateProperties() {
  await executeUpdateProperties(true)
  await refresh()
  $q.dialog({
    title: "Обновление",
    message: "Поля были обновлены"
  })
}

function addProperty() {
  const property = reactive<TaskPropertyDescription>({
    name: "",
    code: "",
    description: "",
    type: "STRING"
  });
  watch(() => property.name, (val: string) => {
    property.code = cyrillicToTranslit().transform(val, "_").toLowerCase()
  })
  additionalProperties.value = additionalProperties.value.concat([property])
}

function removeProperty(property: TaskPropertyDescription) {
  const properties = additionalProperties.value
  const index = properties.indexOf(property);
  if (index !== -1) {
    properties.splice(index, 1);
  }
  additionalProperties.value = properties
}
</script>

<style scoped>

</style>